import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { styled } from 'styled-components';
import { colorsTheme } from 'resources/theme/styled/colors';
import { generateUniqId } from 'helpers/commonHelpers';
import { FormValuesModel } from '.';
import { useEntityIntakeFormContext } from '../../EntityIntakeFormContext';
import { INTAKE_FORM_FIELDS_GRID_SIZES } from 'constants/grids';
import { FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik';
import {
  getPlaceholderForDatePicker,
  getPlaceholderForInputField,
  getPlaceholderForSelect,
} from '../utils';

// components
import FieldIssue from '../FieldIssue';
import LocationForm from './LocationForm';
import SubmitButtons from '../SubmitButtons';
import TrueFalseSelect, {
  IProps as TrueFalseSelectProps,
} from 'components/Forms/FormComponents/SelectInputs/TrueFalseSelect';
import EntityAgeSelect, {
  IProps as EntityAgeSelectProps,
} from 'components/Forms/FormComponents/SelectInputs/CRM/EntityAgeSelect';
import HideIfDisabledForm from 'components/Forms/HideIfDisabledForm';
import NumberOfOfficesSelectIntakeForm, {
  IProps as NumberOfOfficesSelectIntakeFormProps,
} from 'components/Forms/FormComponents/SelectInputs/CRM/NumberOfOfficesSelectIntakeForm';
import NumberOfEmployeesSelectIntakeForm, {
  IProps as NumberOfEmployeesSelectIntakeFormProps,
} from 'components/Forms/FormComponents/SelectInputs/CRM/NumberOfEmployeesSelectIntakeForm';
import CountriesAutocomplete, {
  IProps as CountriesAutocompleteProps,
} from 'components/Forms/FormComponents/Autocompletes/CRM/CountriesAutocomplete';
import {
  Button,
  Col,
  Divider,
  FormDatePicker,
  FormDatePickerProps,
  FormField,
  FormInput,
  FormInputProps,
  FormInputTextArea,
  FormInputTextAreaProps,
  Row,
  SectionIntro,
  Text,
} from '@ui';

const InnerForm = () => {
  const { t } = useTranslation('onboarding');
  const { getActiveIssueByFieldId } = useEntityIntakeFormContext();
  const { values, setFieldValue } = useFormikContext<FormValuesModel>();

  const isRegulatedDependentField = useMemo(() => {
    if (typeof values.isRegulated !== 'boolean') {
      return null;
    }

    return values.isRegulated ? (
      <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
        <FormField<CountriesAutocompleteProps>
          label={
            <FieldIssue fieldId="regulationCountry">
              {t('intake_form.entity_profile.in_which_country')}
            </FieldIssue>
          }
          name="regulationCountry"
          component={CountriesAutocomplete}
          additionalProps={{
            mode: 'multiple',
            initialValue: values.regulationCountryInitialOptions,
            renderPlaceholder: (validationRules) =>
              getPlaceholderForSelect(validationRules, true, true, t),
          }}
        />
      </Col>
    ) : (
      <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
        <FormField<FormInputTextAreaProps>
          label={
            <FieldIssue fieldId="regulationExemption">
              {t('intake_form.entity_profile.exemption_from_regulation')}
            </FieldIssue>
          }
          name="regulationExemption"
          component={FormInputTextArea}
          additionalProps={{
            rows: 5,
            renderPlaceholder: (validationRules) =>
              getPlaceholderForInputField(validationRules, true, t),
          }}
        />
      </Col>
    );
  }, [values.isRegulated]);

  const renderAddresses = (helpers: FieldArrayRenderProps) => {
    const canRemoveAddress = values.addresses.length > 1;

    return (
      <Row gutter={[16, 16]}>
        {values.addresses.map((e, i) => (
          <Col key={e.id} span={24}>
            <LocationForm
              index={i}
              fieldName={`addresses.${i}`}
              deleteCallback={
                canRemoveAddress ? () => helpers.remove(i) : undefined
              }
            />
          </Col>
        ))}
      </Row>
    );
  };

  const addNewAddress = () => {
    const newAddresses = [
      ...values.addresses,
      {
        id: `custom-${generateUniqId()}`,
        type: [],
        country: null,
        city: '',
        street: '',
        state: '',
        postal: '',
      },
    ];
    setFieldValue('addresses', newAddresses);
  };

  const disabledParentFields = useMemo(() => {
    const regulationCountryFieldIssue =
      getActiveIssueByFieldId('regulationCountry');
    const regulationExemptionFieldIssue = getActiveIssueByFieldId(
      'regulationExemption',
    );

    return {
      isRegulated:
        regulationCountryFieldIssue?.isResolved === false ||
        regulationExemptionFieldIssue?.isResolved === false,
    };
  }, [getActiveIssueByFieldId]);

  return (
    <>
      <Row wrap={true} justify="start">
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<CountriesAutocompleteProps>
            disabled
            label={t('intake_form.entity_profile.country_of_incorporation')}
            name="countryOfIncorporation"
            component={CountriesAutocomplete}
            additionalProps={{
              initialValue: values.countryOfIncorporationInitialOption,
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<FormDatePickerProps>
            label={
              <FieldIssue fieldId="entityDateFormation">
                {t('intake_form.entity_profile.date_of_incorporation')}
              </FieldIssue>
            }
            name="dateOfIncorporation"
            component={StyledFormDatePicker}
            additionalProps={{
              disabledDate: (d: any) => !d || (d.isAfter(new Date()) as any),
              renderPlaceholder: (validationRules) =>
                getPlaceholderForDatePicker(validationRules, true, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<EntityAgeSelectProps>
            label={
              <FieldIssue fieldId="entityOperationStart">
                {t('intake_form.entity_profile.entityAge')}
              </FieldIssue>
            }
            name="entityAge"
            component={EntityAgeSelect}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<FormInputProps>
            label={
              <FieldIssue fieldId="registrationNumber">
                {t('intake_form.entity_profile.registration_number')}
              </FieldIssue>
            }
            name="registrationNumber"
            component={FormInput}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForInputField(validationRules, true, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<TrueFalseSelectProps>
            label={
              <FieldIssue
                fieldId="isRegulated"
                isDisabledDueToChildComments={disabledParentFields.isRegulated}
              >
                {t('intake_form.entity_profile.is_regulated')}
              </FieldIssue>
            }
            disabled={disabledParentFields.isRegulated}
            name="isRegulated"
            component={TrueFalseSelect}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        {isRegulatedDependentField}
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<TrueFalseSelectProps>
            label={
              <FieldIssue fieldId="hasBearerShares">
                {t('intake_form.entity_profile.has_bearer_shares')}
              </FieldIssue>
            }
            name="hasBearerShares"
            labelTooltip={t(
              'identification.form_fields.organization.identification_questions.can_issue_bearer_shares_tooltip',
            )}
            component={TrueFalseSelect}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<NumberOfEmployeesSelectIntakeFormProps>
            label={
              <FieldIssue fieldId="numEmployees">
                {t('intake_form.entity_profile.number_of_employees')}
              </FieldIssue>
            }
            name="numberOfEmployees"
            component={NumberOfEmployeesSelectIntakeForm}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<NumberOfOfficesSelectIntakeFormProps>
            label={
              <FieldIssue fieldId="numOffices">
                {t('intake_form.entity_profile.number_of_offices')}
              </FieldIssue>
            }
            name="numberOfOffices"
            component={NumberOfOfficesSelectIntakeForm}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<TrueFalseSelectProps>
            label={
              <FieldIssue fieldId="isOperatingInSanctionedCountries">
                {t(
                  'intake_form.entity_profile.is_operating_in_sanctioned_countries',
                )}
              </FieldIssue>
            }
            name="isOperatingInSanctionedCountries"
            component={TrueFalseSelect}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, false, false, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<FormInputProps>
            label={
              <FieldIssue fieldId="entityWebsite">
                {t('intake_form.entity_profile.company_website')}
              </FieldIssue>
            }
            name="companyWebsite"
            component={FormInput}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForInputField(validationRules, false, t),
            }}
          />
        </Col>
      </Row>
      <SectionIntro
        hideDivider
        title={t('intake_form.entity_profile.locations_section_title')}
        titleColor={colorsTheme.colorWhite}
        titleVariant="h4"
        description={
          <>
            <Text>{t('intake_form.entity_profile.description_line_1')}</Text>
            <StyledUl>
              <li>
                <Text>
                  {t(
                    'identification.organization_address_types.registered_office',
                  )}
                </Text>
              </li>
              <li>
                <Text>
                  {t(
                    'identification.organization_address_types.business_address',
                  )}
                </Text>
              </li>
              <li>
                <Text>
                  {t(
                    'identification.organization_address_types.mailing_address',
                  )}
                </Text>
              </li>
            </StyledUl>
            <Text>{t('intake_form.entity_profile.description_line_2')}</Text>
          </>
        }
      />
      <FieldArray name="addresses" render={renderAddresses} />
      <HideIfDisabledForm hideIfSubmitting>
        <StyledButton size="large" onClick={addNewAddress}>
          {t('intake_form.entity_profile.add_address_btn')}
        </StyledButton>
      </HideIfDisabledForm>
      <HideIfDisabledForm>
        <Divider />
        <SubmitButtons />
      </HideIfDisabledForm>
    </>
  );
};

const StyledFormDatePicker = styled(FormDatePicker)`
  width: 100%;
`;

const StyledUl = styled.ul`
  padding-left: 24px;
`;

const StyledButton = styled(Button)`
  margin-top: ${({ theme }) => theme.marginSm};
`;

export default InnerForm;
